import React from 'react'

import { useLocation } from 'react-router'

import {
  SectionGroup,
  SectionItem,
  SectionItemGrupoParcela,
  SectionItemParcela,
  SectionItemValue,
  type SectionItemProps,
} from '@/components/ui'
import type { SectionItemGrupoParcelaProps, SectionItemParcelaProps } from '@/components/ui/section/section-parcela'
import { formatToBRL, formatToUF } from '@/utils/currency.utils'

interface TItemValue extends SectionItemProps {
  value: number
}

export const createValueItem = (
  label: string,
  value: number,
  colSpan?: 4 | 6 | 8 | 12,
  withRightBorder = false,
): FactoryItem => ({
  type: 'value',
  data: { label, value, colSpan, withRightBorder },
})

export const createParcelaItem = (
  label: string,
  parcela: { data: string; valor: number } | null,
  colSpan?: 4 | 8 | 12,
  withRightBorder = false,
): FactoryItem => ({
  type: 'parcela',
  data: { label, parcela: parcela ?? undefined, colSpan, withRightBorder },
})

export const createGrupoParcelaItem = (
  label: string,
  parcelas: Array<{ valor: number; quantidade: number }> | undefined,
  colSpan?: 4 | 8 | 12,
  withRightBorder = false,
): FactoryItem => ({
  type: 'grupoParcela',
  data: { label, parcelas, colSpan, withRightBorder },
})

type ParcelaData = Omit<SectionItemParcelaProps<{ data: string; valor: number }>, 'formatToCurrency'>

type GrupoParcelaData = Omit<
  SectionItemGrupoParcelaProps<Array<{ valor: number; quantidade: number }>>,
  'formatToCurrency'
>

type FactoryItem =
  | { type: 'value'; data: TItemValue }
  | { type: 'parcela'; data: ParcelaData }
  | { type: 'grupoParcela'; data: GrupoParcelaData }

const renderFactoryItem = (item: FactoryItem, formatToCurrency: (value: number) => string) => {
  switch (item.type) {
    case 'value':
      return (
        <SectionItem label={item.data.label} colSpan={item.data.colSpan} withRightBorder={item.data.withRightBorder}>
          <SectionItemValue>{formatToCurrency(item.data.value)}</SectionItemValue>
        </SectionItem>
      )
    case 'parcela':
      return <SectionItemParcela {...item.data} formatToCurrency={formatToCurrency} />
    case 'grupoParcela':
      return <SectionItemGrupoParcela {...item.data} formatToCurrency={formatToCurrency} />
  }
}

export interface SectionGroupFactoryItem {
  withBorder?: boolean
  items: FactoryItem[]
}

const SectionGroupFactory = ({ withBorder = false, items }: SectionGroupFactoryItem) => {
  const { pathname } = useLocation()
  const formatCurrency = pathname.includes('/es-CL') ? formatToUF : formatToBRL

  return (
    <SectionGroup withBorder={withBorder}>
      {items.map((item) => (
        <React.Fragment key={`${item.type}-${item.data.label}`}>
          {renderFactoryItem(item, formatCurrency)}
        </React.Fragment>
      ))}
    </SectionGroup>
  )
}

export const ValoresSimuladosFactory = ({ grupos }: { grupos: SectionGroupFactoryItem[] }) => (
  <React.Fragment>
    {grupos.map((grupo) => (
      <SectionGroupFactory key={`grupo-${grupo.items[0]?.data.label}`} {...grupo} />
    ))}
  </React.Fragment>
)
